import { Badge, Box, Center, Flex, Heading, ListItem, Spinner, Stack, Text, UnorderedList } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiAlertCircle, FiLock, FiRefreshCw, FiSearch } from "react-icons/fi";
import {
  AnswerKind,
  BidInsightDto,
  BidInsightStatusDto,
  DocumentReadStatus,
  InsightPointDto,
  InsightSourceDto,
  MinimumRequirementReading,
  MissingDocumentFor,
  useGetBidInsightQuery,
  useRequestBidInsightMutation,
} from "../../../../autogen/bff-api";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";
import { NotReadyCard } from "../../NotReadyCard";

const POLL_INTERVAL_MS = 5000;

interface Props {
  eventId: string;
  bidId: string;
}

export const BidInsightTab = ({ eventId, bidId }: Props) => {
  const { t } = useTranslation();
  const { data, error, isLoading, refetch } = useGetBidInsightQuery({ eventId, bidId });
  const inProgress = data?.status === BidInsightStatusDto.Pending || data?.status === BidInsightStatusDto.Running;
  useGetBidInsightQuery({ eventId, bidId }, { pollingInterval: inProgress ? POLL_INTERVAL_MS : 0, skip: !inProgress });
  const [requestInsight, { isLoading: isRequesting, isError: requestFailed }] = useRequestBidInsightMutation();
  const make = () => requestInsight({ eventId, bidId });

  if (isLoading) {
    return (
      <Center py="16">
        <Spinner color="smPrimary" />
      </Center>
    );
  }
  if (error) {
    const bidNotReadable = "status" in error && error.status === 404;
    return bidNotReadable ? (
      <NotReadyCard icon={FiLock} title={t("The AI insight becomes available when you can open the bid.")} />
    ) : (
      <NotReadyCard
        icon={FiAlertCircle}
        title={t("The AI insight could not be loaded.")}
        action={{ label: t("Try again"), onClick: () => refetch() }}
      />
    );
  }

  const insight = data?.insight ?? undefined;
  const failed = data?.status === BidInsightStatusDto.Failed || requestFailed;

  return (
    <Stack spacing="6">
      {inProgress && <NotReadyCard title={t("The AI insight is being made. This takes a minute or two.")} />}
      {!inProgress && failed && (
        <Box role="alert">
          <NotReadyCard
            icon={FiAlertCircle}
            title={t("Could not make the AI insight now. Try again in a little while.")}
            action={{ label: t("Make again"), onClick: make, isLoading: isRequesting }}
          />
        </Box>
      )}
      {!inProgress && !failed && !insight && (
        <NotReadyCard
          icon={FiSearch}
          title={t("AI insight on this bid")}
          description={
            t(
              "The AI reads the bid and points out possible deviations, strengths and gaps, with where in the bid it says so. It does not give assessments or points.",
            ) ?? undefined
          }
          action={{ label: t("Make AI insight"), onClick: make, isLoading: isRequesting }}
        />
      )}
      {insight && !inProgress && (
        <InsightBody insight={insight} outdated={data?.outdated ?? false} onMakeAgain={make} isRequesting={isRequesting} />
      )}
    </Stack>
  );
};

const InsightBody = ({
  insight,
  outdated,
  onMakeAgain,
  isRequesting,
}: {
  insight: BidInsightDto;
  outdated: boolean;
  onMakeAgain: () => void;
  isRequesting: boolean;
}) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();
  return (
    <Stack spacing="6">
      <Card>
        <Text fontWeight="semibold">
          {t("Generated by AI. Check against the bid before you assess. The AI does not give assessments or points.")}
        </Text>
        <Text fontSize="sm" color="smMuted">
          {t("Made {{time}} by {{name}}", { time: displayDateWithTime(insight.generatedAt), name: insight.generatedByName })}
        </Text>
      </Card>
      {outdated && (
        <Box role="status">
          <NotReadyCard
            icon={FiRefreshCw}
            title={t("The bid has changed since the AI insight was made")}
            action={{ label: t("Make again"), onClick: onMakeAgain, isLoading: isRequesting }}
          />
        </Box>
      )}
      {insight.minimumRequirements.length > 0 && (
        <Section title={t("Minimum requirements")}>
          {insight.minimumRequirements.map((requirement) => (
            <Card key={requirement.requirementId}>
              <Flex justify="space-between" gap="3" wrap="wrap">
                <Text fontWeight="semibold">{requirement.title}</Text>
                <ReadingBadge reading={requirement.reading} />
              </Flex>
              <Text fontSize="sm" color="smMuted">
                {t("Supplier's answer")}:{" "}
                {requirement.supplierAnswer == null ? "–" : requirement.supplierAnswer ? t("Yes") : t("No")}
                {requirement.supplierComment ? ` — ${requirement.supplierComment}` : ""}
              </Text>
              {requirement.reason && <Text>{requirement.reason}</Text>}
              <Sources sources={requirement.sources} />
            </Card>
          ))}
        </Section>
      )}
      {insight.awardCriteria.length > 0 && (
        <Section title={t("Evaluation")}>
          {insight.awardCriteria.map((criterion) => (
            <Card key={criterion.criterionId}>
              <Text fontWeight="semibold">{criterion.title}</Text>
              <Text>{criterion.summary}</Text>
              <Points title={t("Strengths")} points={criterion.strengths} />
              <Points title={t("Weaknesses")} points={criterion.weaknesses} />
            </Card>
          ))}
        </Section>
      )}
      <Section title={t("Communication")}>
        <Card>
          {insight.communication.threadCount === 0 ? (
            <Text color="smMuted">{t("There are no questions, clarifications or negotiation messages with this supplier.")}</Text>
          ) : (
            <>
              {insight.communication.summary && <Text>{insight.communication.summary}</Text>}
              <Points
                title={t("Reservations and promises not in the bid itself")}
                points={insight.communication.commitments}
              />
            </>
          )}
        </Card>
      </Section>
      <Section title={t("Documents")}>
        <Card>
          {insight.documents.length === 0 && <Text color="smMuted">{t("The bid has no documents.")}</Text>}
          <UnorderedList spacing="2">
            {insight.documents.map((document) => (
              <ListItem key={document.documentId}>
                <Text as="span" fontWeight="semibold">
                  {document.name}
                </Text>
                {document.description && <Text>{document.description}</Text>}
                <DocumentStatus status={document.readStatus} />
              </ListItem>
            ))}
          </UnorderedList>
          {insight.missingDocuments.length > 0 && (
            <Box>
              <Text fontWeight="semibold" color="smError">
                {t("Required documentation that is missing")}
              </Text>
              <UnorderedList>
                {insight.missingDocuments.map((missing) => (
                  <ListItem key={`${missing.requiredFor}-${missing.title}`}>
                    {missing.requiredFor === MissingDocumentFor.Qualification
                      ? t("Qualification requirement «{{title}}»", { title: missing.title })
                      : t("Award criterion «{{title}}»", { title: missing.title })}
                  </ListItem>
                ))}
              </UnorderedList>
            </Box>
          )}
        </Card>
      </Section>
    </Stack>
  );
};

const Card = ({ children }: { children: React.ReactNode }) => (
  <Stack spacing="2" bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" px="5" py="4">
    {children}
  </Stack>
);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Stack spacing="3">
    <Heading as="h3" size="sm">
      {title}
    </Heading>
    {children}
  </Stack>
);

const ReadingBadge = ({ reading }: { reading: MinimumRequirementReading }) => {
  const { t } = useTranslation();
  switch (reading) {
    case MinimumRequirementReading.SeemsMet:
      return <Badge colorScheme="green">{t("Seems met")}</Badge>;
    case MinimumRequirementReading.PossibleDeviation:
      return <Badge colorScheme="orange">{t("Possible deviation")}</Badge>;
    case MinimumRequirementReading.MissingAnswer:
      return <Badge colorScheme="red">{t("Missing answer")}</Badge>;
  }
};

const DocumentStatus = ({ status }: { status: DocumentReadStatus }) => {
  const { t } = useTranslation();
  switch (status) {
    case DocumentReadStatus.Read:
      return null;
    case DocumentReadStatus.PartlyRead:
      return <Text fontSize="sm" color="smMuted">{t("Partly read by the AI: the documents were too large to read in full")}</Text>;
    case DocumentReadStatus.NotReadOverLimit:
      return <Text fontSize="sm" color="smMuted">{t("Not read by the AI: the documents were too large to read in full")}</Text>;
    case DocumentReadStatus.UnsupportedFormat:
    case DocumentReadStatus.Unreadable:
      return <Text fontSize="sm" color="smError">{t("Could not be read by the AI")}</Text>;
  }
};

const Points = ({ title, points }: { title: string; points: InsightPointDto[] }) => {
  const { t } = useTranslation();
  return (
    <Box>
      <Text fontSize="sm" fontWeight="semibold">
        {title}
      </Text>
      {points.length === 0 ? (
        <Text fontSize="sm" color="smMuted">
          {t("None pointed out")}
        </Text>
      ) : (
        <UnorderedList spacing="1">
          {points.map((point, index) => (
            <ListItem key={index}>
              <Text>{point.text}</Text>
              <Sources sources={point.sources} />
            </ListItem>
          ))}
        </UnorderedList>
      )}
    </Box>
  );
};

const Sources = ({ sources }: { sources: InsightSourceDto[] }) => {
  const describe = useSourceLabel();
  if (sources.length === 0) return null;
  return (
    <Text fontSize="xs" color="smMuted">
      {sources.map(describe).join(" · ")}
    </Text>
  );
};

const useSourceLabel = () => {
  const { t } = useTranslation();
  return (source: InsightSourceDto): string => {
    if (source.documentName) {
      const where =
        source.page != null
          ? t("page {{page}}", { page: source.page })
          : source.slide != null
          ? t("slide {{slide}}", { slide: source.slide })
          : source.sheet ?? source.section ?? null;
      return where ? `${source.documentName}, ${where}` : source.documentName;
    }
    const title = source.answerTitle ?? "";
    switch (source.answerKind) {
      case AnswerKind.MinimumRequirement:
        return t("Answer to minimum requirement «{{title}}»", { title });
      case AnswerKind.Qualification:
        return t("Answer to qualification requirement «{{title}}»", { title });
      case AnswerKind.AwardCriterion:
        return t("Answer to award criterion «{{title}}»", { title });
      case AnswerKind.Price:
        return t("Stated price");
      case AnswerKind.Products:
        return t("Product list");
      case AnswerKind.CoverLetter:
        return t("Cover letter");
      case AnswerKind.Communication:
        return title ? t("Communication «{{title}}»", { title }) : t("Negotiation message");
      default:
        return "";
    }
  };
};
